import React, { useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";

import type { TVL, TVLightProps } from "./types";
import { defaultTimeScaleOptions, getSeriesDefinitionsForType } from "./types";

export const TVLight: React.FC<TVLightProps> = ({ series, options, height = 300 }) => {
  const chartRef = useRef<TVL.IChartApi | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize the chart
  useEffect(() => {
    const initializeChart = () => {
      if (!containerRef.current) {
        return false;
      }

      const containerWidth = containerRef.current.clientWidth;
      if (containerWidth === 0) {
        return false;
      }

      // Clean up any existing chart
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      const chart = createChart(containerRef.current, {
        ...options,
        height: height,
        width: containerWidth,
        timeScale: {
          ...defaultTimeScaleOptions,
          ...((options as Record<string, unknown>)?.timeScale as object),
        },
      });

      chartRef.current = chart;
      return true;
    };

    // Retry mechanism for container width
    const attemptInitialization = () => {
      if (initializeChart()) {
        // Success - add series
        if (!series || !Array.isArray(series)) {
          return;
        }

        addSeriesToChart();
      } else {
        // Retry after a short delay
        setTimeout(attemptInitialization, 50);
      }
    };

    const addSeriesToChart = () => {
      if (!chartRef.current || !series) return;

      series.forEach((serie) => {
        if (!serie.type || !serie.data) {
          return;
        }

        try {
          const seriesOptions = getSeriesDefinitionsForType(serie.type);
          const seriesApi = chartRef.current!.addSeries(seriesOptions, serie.options || {});
          seriesApi.setData(serie.data);
        } catch (error) {
          console.error(`Failed to add TradingView series:`, error);
        }
      });

      chartRef.current!.timeScale().fitContent();
    };

    attemptInitialization();

    // Handle window resize
    const handleResize = () => {
      if (chartRef.current && containerRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [series, height, options]);

  return (
    <div
      ref={containerRef}
      className="TradingViewLight"
      style={{ width: "100%", height: `${height}px` }}
    />
  );
};
